import { createServerFn } from '@tanstack/react-start';
import { getCookie, setCookie } from '@tanstack/react-start/server';
import { supabaseServer } from '../supabase.server';
import type { VoterDetails, VoterDetailsConfig } from '../types';
import { resolveVoterTexts, type VoterTexts } from '../voterTexts';

function getOrCreateDeviceId(): string {
  let deviceId = getCookie('device_id');

  if (!deviceId) {
    deviceId = crypto.randomUUID();
    setCookie('device_id', deviceId, {
      path: '/',
      httpOnly: true,
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax',
      maxAge: 60 * 60 * 24 * 365 * 2, // 2 years
    });
  }

  return deviceId;
}

export interface VotingPageData {
  game: {
    id: string;
    title: string;
    game_date: string;
    game_time: string;
    is_active: boolean;
    voting_active: boolean;
    winners_drawn_at: string | null;
    players: Array<{
      id: string;
      name: string;
      number: number;
      image_url?: string;
    }>;
  } | null;
  existingVote: {
    player_id: string;
    player_name: string;
    player_number: number;
    player_image_url?: string;
  } | null;
  /** Whether this device's vote was drawn as prize winner */
  isWinner: boolean;
  sponsor: {
    name: string | null;
    image_url: string | null;
  } | null;
  /** Which personal details to ask for; null when the admin turned it off */
  voterDetails: VoterDetailsConfig | null;
  /** Teksten op het stemscherm, aangevuld met de standaardteksten */
  texts: VoterTexts;
  deviceId: string;
}

const VOTER_SETTINGS_COLUMNS =
  'collect_voter_details, voter_email_field, voter_name_field, voter_phone_field';

function toVoterDetailsConfig(settings: any): VoterDetailsConfig | null {
  if (!settings?.collect_voter_details) return null;
  return {
    email: settings.voter_email_field,
    name: settings.voter_name_field,
    phone: settings.voter_phone_field,
  };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[0-9 ()-]{8,20}$/;

type VoterDetailsError = 'details_required' | 'invalid_email' | 'invalid_phone';

/** Trims the details, drops fields that are not asked and checks the rest */
function validateVoterDetails(
  config: VoterDetailsConfig | null,
  input: VoterDetails | undefined,
): { details: VoterDetails } | { error: VoterDetailsError } {
  if (!config) return { details: {} };

  const details: VoterDetails = {};
  for (const field of ['email', 'name', 'phone'] as const) {
    const value = input?.[field]?.trim();
    if (config[field] === 'hidden') continue;
    if (!value) {
      if (config[field] === 'required') return { error: 'details_required' };
      continue;
    }
    details[field] = value;
  }

  if (details.email && (details.email.length > 254 || !EMAIL_PATTERN.test(details.email))) {
    return { error: 'invalid_email' };
  }
  if (details.phone && !PHONE_PATTERN.test(details.phone)) {
    return { error: 'invalid_phone' };
  }
  if (details.name && details.name.length > 100) {
    details.name = details.name.slice(0, 100);
  }

  return { details };
}

async function checkIsWinner(gameId: string, deviceId: string) {
  const { data } = await supabaseServer().rpc('is_winner', {
    p_game_id: gameId,
    p_device_id: deviceId,
  });
  return data === true;
}

/**
 * Server function that fetches all data needed for the voting page in one go.
 * Runs on the server, so no waterfall of client-side requests.
 */
export const getVotingPageData = createServerFn({ method: 'GET' }).handler(
  async (): Promise<VotingPageData> => {
    const supabase = supabaseServer();
    const deviceId = getOrCreateDeviceId();

    const now = new Date();
    const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    // Fetch the active game for today and the settings in parallel
    const [{ data: gameData }, { data: settings }] = await Promise.all([
      supabase
        .from('games')
        .select(
          `
          *,
          game_players (
            player:players (*)
          )
        `,
        )
        .eq('game_date', currentDate)
        .eq('is_active', true)
        .order('game_time')
        .limit(1)
        .maybeSingle(),
      // * instead of a column list, so the page keeps its sponsor when a
      // newly added column is not migrated yet
      supabase.from('settings').select('*').maybeSingle(),
    ]);

    const sponsor = settings
      ? { name: settings.sponsor_name, image_url: settings.sponsor_image_url }
      : null;
    const voterDetails = toVoterDetailsConfig(settings);
    const texts = resolveVoterTexts(settings?.voter_texts);

    if (!gameData) {
      return { game: null, existingVote: null, isWinner: false, sponsor, voterDetails, texts, deviceId };
    }

    const game = {
      ...gameData,
      players: gameData.game_players.map((gp: any) => gp.player),
    };

    // Check if this device already voted and, once winners are drawn, won
    const [{ data: vote }, isWinner] = await Promise.all([
      supabase
        .from('user_votes')
        .select('*')
        .eq('game_id', game.id)
        .eq('device_id', deviceId)
        .maybeSingle(),
      game.winners_drawn_at ? checkIsWinner(game.id, deviceId) : false,
    ]);

    return {
      game: {
        id: game.id,
        title: game.title,
        game_date: game.game_date,
        game_time: game.game_time,
        is_active: game.is_active,
        voting_active: game.voting_active,
        winners_drawn_at: game.winners_drawn_at,
        players: game.players,
      },
      existingVote: vote
        ? {
            player_id: vote.player_id,
            player_name: vote.player_name,
            player_number: vote.player_number,
            player_image_url: vote.player_image_url,
          }
        : null,
      isWinner,
      sponsor,
      voterDetails,
      texts,
      deviceId,
    };
  },
);

/**
 * Server function to submit a vote. Checks voting status, duplicate votes and
 * the personal details server-side.
 */
export const submitVote = createServerFn({ method: 'POST' })
  .inputValidator(
    (data: { gameId: string; playerId: string; details?: VoterDetails }) => data,
  )
  .handler(async ({ data }) => {
    const supabase = supabaseServer();
    const deviceId = getOrCreateDeviceId();

    // Check if voting is still active and which details are asked
    const [{ data: game }, { data: settings }] = await Promise.all([
      supabase
        .from('games')
        .select('voting_active')
        .eq('id', data.gameId)
        .single(),
      supabase.from('settings').select(VOTER_SETTINGS_COLUMNS).maybeSingle(),
    ]);

    if (!game?.voting_active) {
      return { success: false, error: 'voting_closed' as const };
    }

    const validated = validateVoterDetails(toVoterDetailsConfig(settings), data.details);
    if ('error' in validated) {
      return { success: false, error: validated.error };
    }

    // Insert the vote together with the details
    const { error } = await supabase.rpc('cast_vote', {
      p_game_id: data.gameId,
      p_player_id: data.playerId,
      p_device_id: deviceId,
      p_email: validated.details.email ?? null,
      p_name: validated.details.name ?? null,
      p_phone: validated.details.phone ?? null,
    });

    if (error) {
      if (error.code === '23505') {
        return { success: false, error: 'already_voted' as const };
      }
      if (error.code === '42501') {
        return { success: false, error: 'voting_closed' as const };
      }
      if (error.code === '23514') {
        // Settings changed between our check and the insert
        return { success: false, error: 'details_required' as const };
      }
      return { success: false, error: 'unknown' as const };
    }

    // Fetch the vote details
    const { data: vote } = await supabase
      .from('user_votes')
      .select('*')
      .eq('game_id', data.gameId)
      .eq('device_id', deviceId)
      .single();

    return {
      success: true,
      vote: vote
        ? {
            player_id: vote.player_id,
            player_name: vote.player_name,
            player_number: vote.player_number,
            player_image_url: vote.player_image_url,
          }
        : null,
    };
  });

/**
 * Server function the voting page calls after the admin (re)drew winners.
 */
export const getWinnerStatus = createServerFn({ method: 'GET' })
  .inputValidator((data: { gameId: string }) => data)
  .handler(async ({ data }) => {
    const deviceId = getCookie('device_id');
    if (!deviceId) return { isWinner: false };
    return { isWinner: await checkIsWinner(data.gameId, deviceId) };
  });
